import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { CheckCircle2, X, Folder, Plus, List, UserPlus, UserCheck, ArrowUpDown, Flag, CalendarClock } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Loader } from '../components/common';
import { TaskCard } from '../components/tasks/TaskCard';
import { useTaskStore } from '../stores/taskStore';
import { useProjectStore } from '../stores/projectStore';
import { useAuthStore } from '../stores/authStore';
import { useUIStore } from '../stores/uiStore';
import { usePageSeen } from '../hooks/usePageSeen';
import type { TaskFilters } from '../stores/taskStore';
import type { Task } from '../types';

type InboxFilterType = 'all' | 'created-by-me' | 'assigned-to-me';
type InboxSortType = 'priority' | 'dueDate';

// Priority weight map: higher value = more urgent (shown first)
const PRIORITY_WEIGHT: Record<string, number> = {
  URGENT: 4,
  HIGH: 3,
  MEDIUM: 2,
  LOW: 1,
};

export const InboxPage: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  // Inbox filter state
  const [inboxFilter, setInboxFilter] = useState<InboxFilterType>('assigned-to-me');
  // Sort state: default by priority (most urgent first)
  const [sortBy, setSortBy] = useState<InboxSortType>('priority');

  // Use stable selectors to prevent re-renders from unstable references
  const tasks = useTaskStore((state) => state.tasks);
  const fetchTasks = useTaskStore((state) => state.fetchTasks);
  const setFilters = useTaskStore((state) => state.setFilters);
  const isLoading = useTaskStore((state) => state.isLoading);
  const filters = useTaskStore((state) => state.filters);
  const clearFilters = useTaskStore((state) => state.clearFilters);
  const projects = useProjectStore((state) => state.projects);
  const fetchProjects = useProjectStore((state) => state.fetchProjects);
  const user = useAuthStore((state) => state.user);
  
  // REV-1 fix: Mark page as seen after content loads
  usePageSeen({ hasLoadedContent: !isLoading });
  
  // Track if we've done the initial fetch for this route to prevent duplicates
  const initialFetchDone = useRef(false);
  const lastPath = useRef(location.pathname);

  // Fetch projects for filter display
  useEffect(() => {
    if (filters.projectId) {
      fetchProjects();
    }
  }, [filters.projectId, fetchProjects]);

  // Fetch inbox tasks - global view showing ALL tasks
  // Backend now handles filtering: shows all tasks (with or without due date)
  // Completed tasks remain visible for 24 hours after completion
  // Sections overlap: tasks can appear in Inbox AND Today/Upcoming at the same time
  useEffect(() => {
    // Only fetch if path changed (route navigation) or this is the first mount
    if (lastPath.current === location.pathname && initialFetchDone.current) {
      return;
    }
    
    lastPath.current = location.pathname;
    initialFetchDone.current = true;
    
    const fetchParams: Record<string, unknown> = { view: 'inbox' };
    // Apply project filter if set
    if (filters.projectId) {
      fetchParams.projectId = filters.projectId;
    }
    // Store filters in state so Header search can preserve them
    setFilters(fetchParams as TaskFilters);
    fetchTasks(fetchParams);
  }, [location.pathname]); // Minimal dependencies - only path change triggers fetch

  // Find the current project for display
  const currentProject = useMemo(() => {
    if (!filters.projectId) return null;
    return projects.find(p => p.id === filters.projectId);
  }, [filters.projectId, projects]);

  // Clear project filter
  const handleClearProjectFilter = () => {
    clearFilters();
    fetchTasks({ view: 'inbox' });
  };

  // Apply inbox filter to tasks before splitting into pending/completed
  const filteredTasks = useMemo(() => {
    if (inboxFilter === 'all' || !user?.id) return tasks;
    if (inboxFilter === 'created-by-me') {
      return tasks.filter(task => task.creatorId === user.id);
    }
    if (inboxFilter === 'assigned-to-me') {
      return tasks.filter(task => task.assigneeId === user.id || task.isUnassigned);
    }
    return tasks;
  }, [tasks, inboxFilter, user?.id]);

  // Separate tasks into pending and completed within 24h
  // Inbox shows ALL tasks (with or without due date)
  // Completed tasks remain visible for 24 hours
  const { pendingTasks, completedTasks, completedByUserCount } = useMemo(() => {
    const pending: Task[] = [];
    const completed: Task[] = [];
    let completedByUser = 0;
    
    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    filteredTasks.forEach(task => {
      if (task.completed && task.completedAt) {
        const completedDate = new Date(task.completedAt);
        // Only show if completed within last 24 hours
        if (completedDate >= twentyFourHoursAgo) {
          completed.push(task);
          // Count tasks completed by the current user
          if (task.completedById === user?.id) {
            completedByUser++;
          }
        }
        // If completed more than 24 hours ago, skip (filtered by backend)
      } else {
        pending.push(task);
      }
    });

    // Sort pending tasks based on selected sort option
    pending.sort((a, b) => {
      if (sortBy === 'priority') {
        // Sort by priority (most urgent first)
        const aWeight = PRIORITY_WEIGHT[a.priority] ?? 0;
        const bWeight = PRIORITY_WEIGHT[b.priority] ?? 0;
        if (aWeight !== bWeight) {
          return bWeight - aWeight; // higher weight (more urgent) first
        }
        // Secondary sort: by due date (closest first), undated last
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      } else {
        // Sort by due date (closest/overdue first), undated last
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      }
    });

    return { pendingTasks: pending, completedTasks: completed, completedByUserCount: completedByUser };
  }, [filteredTasks, user?.id, sortBy]);

  const completedCount = completedTasks.length;
  const hasNoPendingTasks = pendingTasks.length === 0;

  // Count pending (non-completed) tasks for each filter
  const taskCounts = useMemo(() => {
    if (!user?.id) return { all: 0, createdByMe: 0, assignedToMe: 0 };
    const pendingTasks = tasks.filter(task => !task.completed);
    return {
      all: pendingTasks.length,
      createdByMe: pendingTasks.filter(task => task.creatorId === user.id).length,
      assignedToMe: pendingTasks.filter(task => task.assigneeId === user.id || task.isUnassigned).length,
    };
  }, [tasks, user?.id]);

  // Filter button definitions
  const filterButtons: Array<{ key: InboxFilterType; label: string; icon: React.ReactNode; count: number }> = [
    { key: 'assigned-to-me', label: t('inbox.assignedToMe'), icon: <UserCheck className="w-3 h-3 sm:w-3.5 sm:h-3.5" />, count: taskCounts.assignedToMe },
    { key: 'created-by-me', label: t('inbox.createdByMe'), icon: <UserPlus className="w-3 h-3 sm:w-3.5 sm:h-3.5" />, count: taskCounts.createdByMe },
    { key: 'all', label: t('inbox.all'), icon: <List className="w-3 h-3 sm:w-3.5 sm:h-3.5" />, count: taskCounts.all },
  ];

  return (
    <div className="flex flex-col h-full">
      <Header title={t('inbox.title')} showSearch />
      
      <main className="flex-1 overflow-y-auto p-4 sm:p-6">
        {/* Inbox Filter Buttons */}
        <div className="mb-4 flex flex-nowrap gap-1 sm:gap-2" role="group" aria-label={t('inbox.filterTasks')}>
          {filterButtons.map(({ key, label, icon, count }) => (
            <button
              key={key}
              onClick={() => setInboxFilter(key)}
              className={`inline-flex items-center gap-0.5 sm:gap-1 px-1.5 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium transition-colors min-h-[24px] sm:min-h-[28px] whitespace-nowrap ${
                inboxFilter === key
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
              aria-pressed={inboxFilter === key}
            >
              <span className="inline-flex">{icon}</span>
              <span>{label}</span>
              <span className={`inline-flex ml-0.5 px-1 py-0.5 sm:px-1.5 rounded-full text-[8px] sm:text-[10px] font-semibold leading-none ${
                inboxFilter === key
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
              }`}>
                {count}
              </span>
            </button>
          ))}
        </div>

        {/* Sort Toggle Buttons */}
        <div className="mb-4 flex flex-nowrap items-center gap-1 sm:gap-2" role="group" aria-label={t('inbox.sortTasks')}>
          <span className="inline-flex items-center text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap mr-0.5">
            <ArrowUpDown className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
          </span>
          {([
            { key: 'priority' as const, label: t('inbox.sortByPriority'), icon: <Flag className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> },
            { key: 'dueDate' as const, label: t('inbox.sortByDueDate'), icon: <CalendarClock className="w-3 h-3 sm:w-3.5 sm:h-3.5" /> },
          ]).map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => setSortBy(key)}
              className={`inline-flex items-center gap-0.5 sm:gap-1 px-1.5 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium transition-colors min-h-[24px] sm:min-h-[28px] whitespace-nowrap ${
                sortBy === key
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
              aria-pressed={sortBy === key}
            >
              <span className="inline-flex">{icon}</span>
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Project Filter Badge */}
        {currentProject && (
          <div className="mb-4">
            <div 
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer hover:opacity-80 transition-opacity"
              style={{ backgroundColor: `${currentProject.color}20`, color: currentProject.color }}
            >
              <Folder className="w-4 h-4" />
              <span>{currentProject.name}</span>
              <button
                onClick={handleClearProjectFilter}
                className="ml-1 p-0.5 rounded-full hover:bg-black/10 dark:hover:bg-white/10 min-h-[44px] min-w-[44px] flex items-center justify-center"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
        
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader size="md" />
          </div>
        ) : hasNoPendingTasks && completedCount === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="text-4xl mb-4">📥</div>
            <div className="text-gray-900 dark:text-gray-100 font-medium mb-2">
              {inboxFilter === 'all' ? t('inbox.emptyTitle') : inboxFilter === 'created-by-me' ? t('inbox.emptyTitleCreatedByMe') : t('inbox.emptyTitleAssignedToMe')}
            </div>
            <p className="text-sm text-gray-500">
              {inboxFilter === 'all' ? t('inbox.emptySubtitle') : t('inbox.emptySubtitleAlt')}
            </p>
            {inboxFilter === 'all' && (
              <button
                onClick={() => useUIStore.getState().openTaskModal()}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                {t('inbox.createTask')}
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Completed Badge */}
            {completedCount > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg w-fit">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span className="text-sm font-medium text-green-700 dark:text-green-400">
                  {t('inbox.completedLast24h', { count: completedCount })}
                  {completedByUserCount > 0 && (
                    <span className="text-green-700 dark:text-green-400">
                      {' '}· {t('inbox.byYou', { count: completedByUserCount })}
                    </span>
                  )}
                </span>
              </div>
            )}

            {/* Pending Tasks Section */}
            {pendingTasks.length > 0 && (
              <div className="space-y-3">
                {pendingTasks.map((task) => (
                  <TaskCard key={task.id} task={task} />
                ))}
              </div>
            )}

            {/* Completed Tasks Section (within 24h) */}
            {completedTasks.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('inbox.completedSection')}
                </h3>
                {completedTasks.map((task) => (
                  <TaskCard key={task.id} task={task} />
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default InboxPage;
